// components/admin/DemoResetTab.js
// נתוני דמה + איפוס מערכת — CompanySettings Tab 3
//
// API:
//   POST /backup/demo-data → טעינת נתוני דמה
//   POST /backup/reset → איפוס מערכת
//   POST /backup/create → גיבוי לפני פעולה הרסנית
//
// Demo flow:
//   Dialog → checkbox "אני מבין" → "גבה ואז טען" / "המשך ללא גיבוי"
//
// Reset flow (2 steps):
//   Step 1: "האם אתה בטוח?" → "כן, אני בטוח"
//   Step 2: "הקלד 'איפוס'" + checkbox "צור גיבוי" → "אפס עכשיו"

import { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  TextField,
  LinearProgress,
  Divider,
  IconButton,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DatasetIcon from '@mui/icons-material/Dataset';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { useApiMutation } from '../../hooks/useApi';

// =============================================================================
// DemoResetTab
// =============================================================================
export default function DemoResetTab({ showSnackbar }) {
  const { t } = useTranslation();
  const { mutate, loading: mutating } = useApiMutation();

  // ===================================================================
  // Demo Data state
  // ===================================================================
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [demoConfirmed, setDemoConfirmed] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoStep, setDemoStep] = useState(''); // '' | 'backing-up' | 'loading'

  // ===================================================================
  // Reset state
  // ===================================================================
  const [resetStep, setResetStep] = useState(0); // 0=closed, 1=confirm, 2=type keyword
  const [resetKeyword, setResetKeyword] = useState('');
  const [resetBackup, setResetBackup] = useState(true); // checkbox default on
  const [resetLoading, setResetLoading] = useState(false);
  const [resetLoadingStep, setResetLoadingStep] = useState('');

  // ===================================================================
  // Demo Data handlers
  // ===================================================================
  const handleOpenDemo = useCallback(() => {
    setDemoDialogOpen(true);
    setDemoConfirmed(false);
    setDemoStep('');
  }, []);

  const handleDemoWithBackup = useCallback(async () => {
    if (demoLoading) return;
    setDemoLoading(true);

    // Step 1: Backup
    setDemoStep('backing-up');
    const backupResult = await mutate('post', '/backup/create');
    if (!backupResult.success) {
      setDemoLoading(false);
      setDemoStep('');
      showSnackbar(backupResult.error || t('common.error'), 'error');
      return;
    }

    // Step 2: Load demo data
    setDemoStep('loading');
    const result = await mutate('post', '/backup/demo-data');
    if (result.success) {
      showSnackbar(t('companySettings.demoData.loadSuccess'));
      setTimeout(() => window.location.reload(), 1500);
    } else {
      setDemoLoading(false);
      setDemoStep('');
      showSnackbar(result.error || t('common.error'), 'error');
    }
  }, [demoLoading, mutate, showSnackbar, t]);

  const handleDemoWithoutBackup = useCallback(async () => {
    if (demoLoading) return;
    setDemoLoading(true);
    setDemoStep('loading');

    const result = await mutate('post', '/backup/demo-data');
    if (result.success) {
      showSnackbar(t('companySettings.demoData.loadSuccess'));
      setTimeout(() => window.location.reload(), 1500);
    } else {
      setDemoLoading(false);
      setDemoStep('');
      showSnackbar(result.error || t('common.error'), 'error');
    }
  }, [demoLoading, mutate, showSnackbar, t]);

  // ===================================================================
  // Reset handlers
  // ===================================================================
  const handleOpenReset = useCallback(() => {
    setResetStep(1);
    setResetKeyword('');
    setResetBackup(true);
    setResetLoadingStep('');
  }, []);

  const handleResetStep1Confirm = useCallback(() => {
    setResetStep(2);
  }, []);

  const handleResetExecute = useCallback(async () => {
    if (resetLoading) return;
    setResetLoading(true);

    // Optional backup first
    if (resetBackup) {
      setResetLoadingStep('backing-up');
      const backupResult = await mutate('post', '/backup/create');
      if (!backupResult.success) {
        setResetLoading(false);
        setResetLoadingStep('');
        showSnackbar(backupResult.error || t('common.error'), 'error');
        return;
      }
    }

    // Execute reset
    setResetLoadingStep('resetting');
    const result = await mutate('post', '/backup/reset');
    if (result.success) {
      showSnackbar(t('companySettings.systemReset.resetSuccess'));
      setTimeout(() => window.location.reload(), 1500);
    } else {
      setResetLoading(false);
      setResetLoadingStep('');
      showSnackbar(result.error || t('common.error'), 'error');
    }
  }, [resetLoading, resetBackup, mutate, showSnackbar, t]);

  const resetKeywordMatch = resetKeyword === t('companySettings.systemReset.step2Keyword');

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <Box data-testid="settings__demo-reset-tab">

      {/* ============================================================= */}
      {/* Demo Data Section                                              */}
      {/* ============================================================= */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <DatasetIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            {t('companySettings.demoData.title')}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('companySettings.demoData.description')}
        </Typography>

        <Button
          data-testid="settings__demo__button--load"
          variant="contained"
          color="warning"
          size="large"
          startIcon={<DatasetIcon />}
          onClick={handleOpenDemo}
        >
          {t('companySettings.demoData.loadButton')}
        </Button>
      </Paper>

      {/* ============================================================= */}
      {/* System Reset Section                                           */}
      {/* ============================================================= */}
      <Paper variant="outlined" sx={{ p: 3, border: '1px solid', borderColor: 'error.light' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <DeleteForeverIcon color="error" />
          <Typography variant="subtitle1" fontWeight={600} color="error.main">
            {t('companySettings.systemReset.title')}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('companySettings.systemReset.description')}
        </Typography>

        <Button
          data-testid="settings__reset__button--start"
          variant="contained"
          color="error"
          size="large"
          startIcon={<DeleteForeverIcon />}
          onClick={handleOpenReset}
        >
          {t('companySettings.systemReset.resetButton')}
        </Button>
      </Paper>

      {/* ============================================================= */}
      {/* Demo Data Dialog                                               */}
      {/* ============================================================= */}
      <Dialog
        open={demoDialogOpen}
        onClose={() => !demoLoading && setDemoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="settings__demo__dialog"
      >
        {demoLoading && <LinearProgress />}

        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          {t('companySettings.demoData.confirmTitle')}
          <Box sx={{ flex: 1 }} />
          <IconButton aria-label={t('common.close')} size="small" onClick={() => setDemoDialogOpen(false)} disabled={demoLoading}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('companySettings.demoData.confirmMessage')}
          </Alert>

          <FormControlLabel
            data-testid="settings__demo__checkbox--confirm"
            control={
              <Checkbox
                checked={demoConfirmed}
                onChange={(e) => setDemoConfirmed(e.target.checked)}
                disabled={demoLoading}
              />
            }
            label={t('companySettings.demoData.confirmCheckbox')}
          />

          {demoLoading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {demoStep === 'backing-up'
                ? t('companySettings.backup.createBackup') + '...'
                : t('companySettings.demoData.loadButton') + '...'
              }
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setDemoDialogOpen(false)} disabled={demoLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="settings__demo__button--without-backup"
            variant="outlined"
            color="warning"
            onClick={handleDemoWithoutBackup}
            disabled={demoLoading || !demoConfirmed}
          >
            {t('companySettings.demoData.loadWithoutBackup')}
          </Button>
          <Button
            data-testid="settings__demo__button--with-backup"
            variant="contained"
            onClick={handleDemoWithBackup}
            disabled={demoLoading || !demoConfirmed}
          >
            {t('companySettings.demoData.backupAndLoad')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================= */}
      {/* Reset Dialog — Step 1                                          */}
      {/* ============================================================= */}
      <Dialog
        open={resetStep === 1}
        onClose={() => setResetStep(0)}
        maxWidth="sm"
        fullWidth
        data-testid="settings__reset__dialog--step1"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="error" />
          {t('companySettings.systemReset.step1Title')}
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="error">
            {t('companySettings.systemReset.step1Message')}
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setResetStep(0)}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="settings__reset__button--step1-confirm"
            variant="contained"
            color="error"
            onClick={handleResetStep1Confirm}
          >
            {t('companySettings.systemReset.step1Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================= */}
      {/* Reset Dialog — Step 2                                          */}
      {/* ============================================================= */}
      <Dialog
        open={resetStep === 2}
        onClose={() => !resetLoading && setResetStep(0)}
        maxWidth="sm"
        fullWidth
        data-testid="settings__reset__dialog--step2"
      >
        {resetLoading && <LinearProgress />}

        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="error" />
          {t('companySettings.systemReset.step2Title')}
          <Box sx={{ flex: 1 }} />
          <IconButton aria-label={t('common.close')} size="small" onClick={() => setResetStep(0)} disabled={resetLoading}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('companySettings.systemReset.step2Message')}
          </Typography>

          <TextField
            data-testid="settings__reset__input--keyword"
            value={resetKeyword}
            onChange={(e) => setResetKeyword(e.target.value)}
            fullWidth
            size="small"
            placeholder={t('companySettings.systemReset.step2Input')}
            disabled={resetLoading}
            error={resetKeyword.length > 0 && !resetKeywordMatch}
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 1.5 }} />

          <FormControlLabel
            data-testid="settings__reset__checkbox--backup"
            control={
              <Checkbox
                checked={resetBackup}
                onChange={(e) => setResetBackup(e.target.checked)}
                disabled={resetLoading}
              />
            }
            label={t('companySettings.systemReset.backupBeforeReset')}
          />

          {resetLoading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {resetLoadingStep === 'backing-up'
                ? t('companySettings.backup.createBackup') + '...'
                : t('companySettings.systemReset.resetButton') + '...'
              }
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setResetStep(0)} disabled={resetLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="settings__reset__button--step2-confirm"
            variant="contained"
            color="error"
            onClick={handleResetExecute}
            disabled={resetLoading || !resetKeywordMatch}
          >
            {t('companySettings.systemReset.step2Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
